import { yupResolver } from '@hookform/resolvers/yup'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import Button from '@/components/Button'
import type { FormData } from '@/pages/getRecipe/allInOne/utils/formTypes'
import {
  FORM_FIELDS,
  validationSchema,
} from '@/pages/getRecipe/allInOne/utils/formTypes'
import { type RootState, useAppSelector } from '@/store'
import { setAllInOneRecipeOutput, setRecipeActiveStep } from '@/store/allInOne'
import {
  useGenerateAllInOneRecipeMutation,
  useGetMetadataQuery,
} from '@/store/apiService'
import {
  ImageCalibrationStatus,
  setImageCalibrationStatus,
} from '@/store/sessionSlice'

import { DENTIN_TYPE, imageType } from '../utils/constants'
import Indication from './Indication'
import MeasureColor from './MeasureColor'

interface Metadata {
  required_inputs: any
  [key: string]: any
}

const AllInOnePanel = ({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement>
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'buttons',
  })
  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'validation',
  })
  const navigate = useNavigate()
  const location = useLocation()
  const formValues = useSelector((state: RootState) => state.allInOne.form)
  const recipeActiveStep = useSelector(
    (state: RootState) => state.allInOne.recipeActiveStep,
  )
  const canvasImage = useSelector((state: RootState) => state.app.canvasImage)
  const greyCardValue = useSelector(
    (state: RootState) => state.app.greyCardValue,
  )
  const currentCaseId = useSelector(
    (state: RootState) => state.session.currentCaseId,
  )

  const dispatch = useDispatch()

  const [generateAllInOneRecipe] = useGenerateAllInOneRecipeMutation()

  const [requiredInputValidation, setRequiredInputValidation] = useState<
    Record<string, boolean>
  >({
    [FORM_FIELDS.CAPBRAND]: false,
    [FORM_FIELDS.PREP_COLOR]: false,
    [FORM_FIELDS.DENTIN_COLOR]: false,
    [FORM_FIELDS.NATURAL_DIE_MATERIAL]: false,
  })

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
    setValue,
    clearErrors,
  } = useForm<FormData>({
    defaultValues: formValues || {},
    resolver: yupResolver(validationSchema()),
    mode: 'onChange',
    context: {
      requiredInputValidation,
      isCapBrandRequired: requiredInputValidation[FORM_FIELDS.CAPBRAND],
    },
  })

  const { data: metaData } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as { data: Metadata | undefined; error: any; isLoading: boolean }

  const indicationValue = useAppSelector(
    (state) => state.allInOne.form[FORM_FIELDS.INDICATION],
  )
  const validation = (value: any) => {
    const validated = {
      [FORM_FIELDS.CAPBRAND]: metaData?.required_inputs[value].capbrand,
      [FORM_FIELDS.PREP_COLOR]: metaData?.required_inputs[value].prep_colors,
      [FORM_FIELDS.DENTIN_COLOR]:
        metaData?.required_inputs[value].target_colors,
      [FORM_FIELDS.NATURAL_DIE_MATERIAL]:
        metaData?.required_inputs[value].estethic_model,
    }
    setRequiredInputValidation(validated)
  }

  useEffect(() => {
    if (metaData?.required_inputs) {
      if (indicationValue) {
        validation(indicationValue)
      }
    }
  }, [metaData?.required_inputs, indicationValue])

  const recipeTypeValue = watch(FORM_FIELDS.RECIPE_TYPE) || ''

  const onNext = useCallback(async () => {
    let isValid = false
    switch (recipeActiveStep) {
      case 0: {
        const indicationFieldsToValidate: (keyof FormData)[] = [
          FORM_FIELDS.RECIPE_TYPE,
          FORM_FIELDS.INDICATION,
          FORM_FIELDS.BRAND,
          FORM_FIELDS.NATURAL_DIE_MATERIAL,
          FORM_FIELDS.CAPBRAND,
          FORM_FIELDS.STAININGBRAND,
          FORM_FIELDS.AVAILSPACE_MIDDLE,
        ]

        if (recipeTypeValue === DENTIN_TYPE.TRIPLE_DENTIN) {
          indicationFieldsToValidate.push(
            FORM_FIELDS.AVAILSPACE_CERVICAL,
            FORM_FIELDS.AVAILSPACE_INCISAL,
          )
        }

        if (!canvasImage?.image) {
          toast.error(translate('selectImage'))
          break
        }

        if (
          canvasImage?.type === imageType.RAW &&
          !canvasImage?.calibrated_image
        ) {
          toast.error(translate('calibrateImage'))
          break
        }

        //Reset calibartion state

        dispatch(
          setImageCalibrationStatus({
            id: canvasImage?.id,
            status: ImageCalibrationStatus.CALIBRATE,
          }),
        )

        isValid = await trigger(indicationFieldsToValidate)
        break
      }
      case 1: {
        isValid = await trigger([FORM_FIELDS.SKINENAMELTYPE])
        break
      }
    }

    if (isValid) {
      dispatch(setRecipeActiveStep(recipeActiveStep + 1))
    }
  }, [dispatch, recipeActiveStep, trigger, watch, canvasImage])

  const onBack = useCallback(() => {
    dispatch(setRecipeActiveStep(recipeActiveStep - 1))
  }, [dispatch, recipeActiveStep])

  const onSubmit = useCallback(
    (data: FormData) => {
      try {
        // Handle form submission
        const {
          gray_card,
          availspace_cervical,
          availspace_middle,
          availspace_incisal,
          prepcolor_cervical,
          prepcolor_incisal,
          targetcervical,
          targetincisal,
          skinenameltype,
          dentinColor,
          prepColor,
          prepcolor_middle,
          ...rest
        } = data
        const payload = {
          ...rest,
          protocol:
            canvasImage?.type === imageType.OPTISHADE
              ? 'Optishade'
              : `L${canvasImage?.calibrate_meta?.protocol || 79}`,

          availspace_middle: `${Number(availspace_middle).toFixed(1)} mm`,
          skinenameltype,
          skinenameltype_cervical: skinenameltype,
          skinenameltype_middle: skinenameltype,
          skinenameltype_incisal: skinenameltype,

          ...(requiredInputValidation[FORM_FIELDS.PREP_COLOR] && {
            prepcolor_middle,
          }),

          ...(data.recipe_type === DENTIN_TYPE.TRIPLE_DENTIN && {
            availspace_cervical: `${Number(availspace_cervical).toFixed(1)} mm`,
            availspace_incisal: `${Number(availspace_incisal).toFixed(1)} mm`,
            targetcervical,
            targetincisal,

            ...(requiredInputValidation[FORM_FIELDS.PREP_COLOR] && {
              prepcolor_middle,
              prepcolor_cervical,
              prepcolor_incisal,
            }),
          }),
        }

        generateAllInOneRecipe({ caseId: currentCaseId || '', data: payload })
          .unwrap()
          .then((res) => {
            dispatch(setAllInOneRecipeOutput(res))
            dispatch(setRecipeActiveStep(0))
            // dispatch(resetAllInOneState())
            navigate(`/all-in-one-output${location.search}`)
          })
          .catch((e) => {
            toast.error(e?.data?.message || 'Error generating recipe')
          })
      } catch (error) {
        console.error('Form submission error:', error)
      }
    },
    [requiredInputValidation, canvasImage, imageType],
  )

  const renderStepContent = useMemo(() => {
    switch (recipeActiveStep) {
      case 0:
        return (
          <Indication
            control={control}
            recipeTypeValue={recipeTypeValue}
            requiredInputValidation={requiredInputValidation}
          />
        )
      case 1:
        return (
          <MeasureColor
            control={control}
            recipeTypeValue={recipeTypeValue}
            setValue={setValue}
            clearErrors={(name?: keyof FormData | (keyof FormData)[]) =>
              clearErrors(name)
            }
            errors={errors}
            requiredInputValidation={requiredInputValidation}
            containerRef={containerRef}
          />
        )
      default:
        return null
    }
  }, [
    recipeActiveStep,
    control,
    recipeTypeValue,
    errors,
    requiredInputValidation,
  ])

  const renderButtons = useMemo(
    () => (
      <Box px={2} py={5} display="flex" gap={2}>
        {recipeActiveStep > 0 && (
          <Button
            label={t('back')}
            variant="outlined"
            onClick={onBack}
            startIcon={<ChevronLeftIcon />}
            textTransform="uppercase"
            sx={recipeActiveStep === 2 ? {} : { flexGrow: 1 }}
          />
        )}
        {recipeActiveStep < 1 ? (
          <Button
            label={t('next')}
            onClick={onNext}
            endIcon={<ChevronRightIcon />}
            textTransform="uppercase"
            sx={{ flexGrow: 1 }}
          />
        ) : (
          <Button
            label={t('generateRecipe')}
            onClick={handleSubmit(onSubmit)}
            textTransform="uppercase"
            sx={{ flexGrow: 2 }}
          />
        )}
      </Box>
    ),
    [recipeActiveStep, onBack, onNext, handleSubmit, onSubmit],
  )

  return (
    <Box display="flex" flexDirection="column" flexGrow={1} overflow="auto">
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        gap={1}
        overflow="auto"
      >
        {renderStepContent}
      </Box>
      {renderButtons}
    </Box>
  )
}

export default AllInOnePanel
